import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import ConfirmDialog from './ConfirmDialog';

const errorText = (err, fallback) => err?.response?.data?.message || fallback;

// Admin-only card (the caller decides who sees it; the server enforces it
// regardless). Controls the server-side "Sensitive Data Protection" setting
// that applies to every request sent to the AI provider.
export default function DataPrivacyCard() {
  const { show } = useToast();
  const [enabled, setEnabled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api.get('/settings/ai-privacy')
      .then((res) => setEnabled(!!res.data.data.enabled))
      .catch((err) => setLoadError(errorText(err, 'Failed to load the data protection setting')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // The switch only moves after the server confirms, so a failed save leaves
  // the previous state on screen instead of showing something untrue.
  const save = async (next) => {
    setSaving(true);
    try {
      const res = await api.put('/settings/ai-privacy', { enabled: next });
      setEnabled(!!res.data.data.enabled);
      show(next ? 'Sensitive Data Protection turned on' : 'Sensitive Data Protection turned off', next ? 'success' : 'info');
    } catch (err) {
      show(errorText(err, 'Could not save the setting — it was not changed'), 'error');
    } finally {
      setSaving(false);
      setConfirmOff(false);
    }
  };

  const onSwitch = () => {
    if (saving || enabled === null) return;
    if (enabled) setConfirmOff(true); // turning OFF needs a confirmation
    else save(true);
  };

  const purge = async () => {
    setPurging(true);
    try {
      const res = await api.delete('/settings/ai-privacy/stored-data');
      const { deleted, failed } = res.data.data;
      if (failed > 0) show(`${failed} stored AI chat${failed === 1 ? '' : 's'} could not be deleted (${deleted} deleted)`, 'error');
      else show(`Deleted ${deleted} stored AI chat${deleted === 1 ? '' : 's'}`, 'success');
    } catch (err) {
      show(errorText(err, 'Could not delete stored AI chats'), 'error');
    } finally {
      setPurging(false);
      setConfirmPurge(false);
    }
  };

  return (
    <div className="card priv-card" style={{ marginBottom: '1.5rem' }}>
      <div className="ct"><i className="fa-solid fa-user-shield" /> Data privacy &amp; security</div>

      {loading && (
        <div role="status" style={{ color: 'var(--slate)', fontSize: 'var(--text-sm)', padding: '.5rem 0' }}>
          <i className="fa-solid fa-spinner fa-spin" /> Loading privacy settings…
        </div>
      )}

      {loadError && !loading && (
        <div className="alert" role="alert">
          <i className="fa-solid fa-triangle-exclamation" />
          <div className="alert-t">{loadError}</div>
          <button type="button" className="btn btn-outline btn-sm" onClick={load}><i className="fa-solid fa-rotate-right" /> Retry</button>
        </div>
      )}

      {!loading && !loadError && enabled !== null && (
        <>
          <div className={`priv-status ${enabled ? '' : 'off'}`}>
            <span className="priv-ico" aria-hidden="true">
              <i className={`fa-solid ${enabled ? 'fa-shield-halved' : 'fa-shield'}`} />
            </span>
            <div className="priv-body">
              <div className="priv-head">
                <div className="set-l" id="ai-protect-label" style={{ marginBottom: 0 }}>Sensitive Data Protection</div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  aria-labelledby="ai-protect-label"
                  className={`toggle ${enabled ? 'on' : ''}`}
                  disabled={saving}
                  onClick={onSwitch}
                />
                <span className={`pill ${enabled ? 'p-ok' : 'p-conf'}`}>
                  <i className={`fa-solid ${enabled ? 'fa-circle-check' : 'fa-triangle-exclamation'}`} /> {enabled ? 'On' : 'Off'}
                </span>
              </div>
              <div className="set-d" style={{ marginTop: '.25rem' }}>No personal data is sent to OpenAI.</div>
            </div>
          </div>

          <ul className="priv-list">
            <li><i className="fa-solid fa-circle-check" /> OpenAI keeps no copy of what is sent (storage is switched off on every request).</li>
            <li><i className="fa-solid fa-circle-check" /> Names and IDs are replaced with labels such as “Trainer 1”.</li>
            <li><i className="fa-solid fa-circle-check" /> Emails, phone numbers, ID numbers and card numbers are hidden before sending, so they are never stored by OpenAI.</li>
          </ul>

          {!enabled && (
            <div className="tt-alert" role="alert" style={{ marginBottom: '.9rem' }}>
              <i className="fa-solid fa-triangle-exclamation" />
              <div className="tt-alert-t">Protection is off — real names and details are being sent to OpenAI.</div>
            </div>
          )}

          <div className="set-row" style={{ marginTop: '.5rem' }}>
            <div>
              <div className="set-l">Stored AI chats</div>
              <div className="set-d">Removes chats OpenAI saved before this protection was added.</div>
            </div>
            <button type="button" className="btn btn-danger btn-sm" onClick={() => setConfirmPurge(true)} disabled={purging}>
              <i className={`fa-solid ${purging ? 'fa-spinner fa-spin' : 'fa-trash-can'}`} /> {purging ? 'Deleting…' : 'Delete stored AI chats'}
            </button>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmOff}
        title="Turn off Sensitive Data Protection?"
        message="Real names, IDs and contact details will be sent to OpenAI. Only do this if you are sure."
        confirmLabel="Turn off"
        busy={saving}
        onConfirm={() => save(false)}
        onCancel={() => { if (!saving) setConfirmOff(false); }}
      />
      <ConfirmDialog
        open={confirmPurge}
        title="Delete stored AI chats?"
        message="This cannot be undone. OpenAI's own safety record is not affected."
        confirmLabel="Delete"
        busy={purging}
        onConfirm={purge}
        onCancel={() => { if (!purging) setConfirmPurge(false); }}
      />
    </div>
  );
}
